import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import Button from '@pb/kit-component-button';
import {
    Drawer,
    DrawerFooter,
    DrawerHeader,
    DrawerHole,
    DrawerMain,
} from '@pb/kit-component-drawer';

import {DrawerHeader as EntitlementDrawerHeader} from './components/drawer-header';
import {closeDrawer, selectDrawerDetails} from './entitlement-drawer.slice';
import {EntitlementType as EntitlementTypes} from './typedef';
import {GrantView} from './views/grant.view';
import {NotAssociatedView} from './views/not-associated-view';
import {PresetView} from './views/preset.view';
import {ResourceView} from './views/resource.view';
import {RoleView} from './views/role.view';

type Props = {
    stickyToElement: string,
    onEditResource?: (resourceId: string) => void,
    onEditGrant?: (grantId: string) => void,
    onEditRole?: (roleId: string) => void,
    onEditPreset?: (presetId: string) => void,
};

export const EntitlementsDrawer = (props: Props) => {
    const {stickyToElement, onEditResource, onEditGrant, onEditRole, onEditPreset} = props;
    const dispatch = useDispatch();
    const location = useLocation();
    const stickyElementRef = useRef<HTMLElement | null>(null);
    const {shown, history} = useSelector(selectDrawerDetails);

    useEffect(() => {
        stickyElementRef.current = document.querySelector(stickyToElement);
    }, [stickyToElement, location.pathname]);

    useEffect(() => {
        return () => {
            dispatch(closeDrawer());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const closeDrawerHandler = useCallback(() => {
        dispatch(closeDrawer());
    }, [dispatch]);

    const handleSetOpen = useCallback((open: boolean) => {
        if (!open) {
            closeDrawerHandler();
        }
    }, [closeDrawerHandler]);

    const currentEntitlement = useMemo(() => (
        history.length ? history[history.length - 1] : null
    ), [history]);

    const footer = useMemo(() => {
        let footerView;

        if (currentEntitlement && currentEntitlement.entitlementType === EntitlementTypes.RESOURCE && onEditResource) {
            footerView = (
                <Button variant="Type.PRIMARY" onClick={() => onEditResource(currentEntitlement?.entitlementId)}>
                    Edit
                </Button>
            );
        }

        if (currentEntitlement && currentEntitlement.entitlementType === EntitlementTypes.GRANT && onEditGrant) {
            footerView = (
                <Button variant="Type.PRIMARY" onClick={() => onEditGrant(currentEntitlement?.entitlementId)}>
                    Edit
                </Button>
            );
        }

        if (currentEntitlement && currentEntitlement.entitlementType === EntitlementTypes.ROLE && onEditRole) {
            footerView = (
                <Button variant="Type.PRIMARY" onClick={() => onEditRole(currentEntitlement?.entitlementId)}>
                    Edit
                </Button>
            );
        }

        if (currentEntitlement && currentEntitlement.entitlementType === EntitlementTypes.PRESET && onEditPreset) {
            footerView = (
                <Button variant="Type.PRIMARY" onClick={() => onEditPreset(currentEntitlement?.entitlementId)}>
                    Edit
                </Button>
            );
        }

        return footerView;
    }, [currentEntitlement, onEditGrant, onEditPreset, onEditResource, onEditRole]);

    return (
        <Drawer
            open={shown}
            setOpen={handleSetOpen}
            placement="right"
            stickToElement={stickyElementRef.current || undefined}
            style={{width: 'fit-content'}}>
            <DrawerHeader>
                {currentEntitlement ? <EntitlementDrawerHeader/> : <div/>}
            </DrawerHeader>
            <DrawerMain>
                <DrawerHole>
                    {currentEntitlement?.entitlementType === EntitlementTypes.RESOURCE ? (
                        <ResourceView
                            key={currentEntitlement.entitlementId}
                            resourceId={currentEntitlement.entitlementId}
                        />
                    ) : null}
                    {currentEntitlement?.entitlementType === EntitlementTypes.GRANT ? (
                        <GrantView
                            key={currentEntitlement.entitlementId}
                            grantId={currentEntitlement.entitlementId}
                        />
                    ) : null}
                    {currentEntitlement?.entitlementType === EntitlementTypes.ROLE ? (
                        <RoleView
                            key={currentEntitlement.entitlementId}
                            roleId={currentEntitlement.entitlementId}
                        />
                    ) : null}
                    {currentEntitlement?.entitlementType === EntitlementTypes.PRESET ? (
                        <PresetView
                            key={currentEntitlement.entitlementId}
                            presetId={currentEntitlement.entitlementId}
                            relations={currentEntitlement.presetRelations || []}
                        />
                    ) : null}
                    {currentEntitlement?.entitlementType === EntitlementTypes.NOT_ASSOCIATED ? (
                        <NotAssociatedView
                            key={currentEntitlement.entitlementId}
                            currentEntitlement={currentEntitlement}
                        />
                    ) : null}
                </DrawerHole>
            </DrawerMain>
            {footer ? (
                <DrawerFooter>
                    {footer}
                </DrawerFooter>
            ) : null}
        </Drawer>
    );
};
